// Stadium Trial — spell catalogue for the A/B "spells" variant (variant B).
//
// Spells replace the boost draft in variant B: the player picks exactly
// SPELL_LOADOUT_SIZE spells ONCE at run start (persisted in pve_spell_loadout,
// reset on defeat/clear like pve_active_bonuses). Unlocks are derived from the
// player LEVEL (levelFromXp) — deterministic, nothing stored in DB.
//
// Each spell carries a typed `effects` object consumed by the interactive
// combat engine (engine/pveCombatInteractive.ts). Effects REALLY change the
// fight — they are never just UI text.
//
// Combo design (v3, 2026-07-07): Focus buffs ALL your offensive spells for the
// turn (CD 2), Feint unbalances the enemy for your next hit, Nutmeg feeds
// energy through Technicité (crit), Vision echoes damage when the enemy SPENDS
// energy, Wall is a flat shield that GROWS on enemy crits received. Several
// spells are once-per-turn; cooldowns tick at player turn start.
//
// To add a spell: add an id to PveSpellId, an entry to PVE_SPELLS (with
// nameFr/descFr + nameKo/descKo), and — if it introduces a brand-new mechanic —
// a hook in pveCombatInteractive's castSpell.

export type PveSpellId =
  | "classic_strike"
  | "focus"
  | "nutmeg"
  | "feint"
  | "reckless_strike"
  | "lifesteal"
  | "vision"
  | "wall";

/** How many spells the player locks in for the whole run (free pick — the
 *  mandatory Classic Strike was removed with the rotation mode). */
export const SPELL_LOADOUT_SIZE = 4;

/** All effect fields a spell may declare. All optional — a spell sets only its own. */
export interface PveSpellEffects {
  // Damage spells — % of a base attack hit (Puissance drives spells AND autos).
  damagePercent?: number;           // 100 = one normal hit

  // Focus — buff on YOURSELF: ALL your offensive spells deal bonus damage for
  // the REST OF THIS TURN (cleared at turn end, not consumed per cast).
  turnDamageBonusPercent?: number;

  // Usage limit: castable only once per player turn (resets at turn start).
  oncePerTurn?: boolean;

  // Feint — debuff on the ENEMY: your next offensive spell deals bonus FINAL
  // damage. Applied AFTER this spell's own hit; overwrites, no stacking.
  applyOffBalancePercent?: number;

  // Reckless Strike — HP cost paid on cast (uncastable if it would kill you).
  selfDamagePercentMaxHp?: number;

  // Crit riders.
  critEnergyRefund?: number;        // Nutmeg: energy gained when this spell crits
  healOnCritPercentMaxHp?: number;  // Lifesteal: % max HP healed when this spell crits

  // Vision — ECHO mark: at the end of the enemy's NEXT turn, it takes +X% of
  // your damage per energy IT SPENT that turn (0 spent = no echo). Punishes the
  // enemy's casts — its gauge is visible, so the timing is a read.
  echoPerEnemyEnergyPercent?: number;

  // Wall — flat shield lasting shieldDurationTurns player turns (expires at the
  // start of your Nth next turn). While active, every enemy CRIT you receive
  // adds shieldPerEnemyCrit. Recast replaces it.
  shieldFlat?: number;
  shieldPerEnemyCrit?: number;
  shieldDurationTurns?: number;

  // Cooldown in player turns (cast on turn N → castable again on turn N+cd).
  cooldownTurns?: number;
}

export interface PveSpellDef {
  id: PveSpellId;
  name: string;
  description: string;
  nameFr?: string;        // French (Stadium Trial i18n) — falls back to `name`
  descFr?: string;        // French — falls back to `description`
  nameKo?: string;        // Korean — falls back to `name`
  descKo?: string;        // Korean — falls back to `description`
  icon: string;   // emoji
  color: string;  // accent (text / border)
  bg: string;     // subtle tint (chip / selected background)
  /** Energy cost per cast (income is +2/turn, cap 6). */
  cost: number;
  /** Player level at which the spell becomes pickable (derived from levelFromXp). */
  unlockLevel: number;
  /** Always in the loadout — pre-selected at spell-select, cannot be removed. */
  mandatory?: boolean;
  effects: PveSpellEffects;
}

export const PVE_SPELLS: Record<PveSpellId, PveSpellDef> = {
  classic_strike: {
    id: "classic_strike",
    name: "Classic Strike",
    description: "Deal 65% attack damage.",
    nameFr: "Frappe classique",
    descFr: "Inflige 65% des dégâts d'attaque.",
    nameKo: "기본 타격",
    descKo: "공격 데미지의 65%를 입힙니다.",
    icon: "⚔️",
    color: "#9FB0C8",
    bg: "rgba(159,176,200,0.12)",
    cost: 3,
    unlockLevel: 1,
    effects: { damagePercent: 65 },
  },
  focus: {
    id: "focus",
    name: "Focus",
    description: "Your next offensive spell deals +15% damage, then your power returns to normal.",
    nameFr: "Concentration",
    descFr: "Ton prochain sort offensif inflige +15% de dégâts, puis ta puissance revient à la normale.",
    nameKo: "집중",
    descKo: "다음 공격 스펠의 데미지가 +15% 증가한 뒤 능력치가 원래대로 돌아옵니다.",
    icon: "🧠",
    color: "#C084FC",
    bg: "rgba(192,132,252,0.12)",
    cost: 1,
    unlockLevel: 1,
    effects: { turnDamageBonusPercent: 15 },
  },
  nutmeg: {
    id: "nutmeg",
    name: "Nutmeg",
    description: "Deal 40% attack damage. On crit, gain 1 mana. Once per turn.",
    nameFr: "Petit pont",
    descFr: "Inflige 40% des dégâts d'attaque. Si coup critique, gagne 1 mana. 1 fois par tour.",
    nameKo: "알까기",
    descKo: "공격 데미지의 40%를 입힙니다. 치명타 시 마나 1을 얻습니다. 턴당 1회.",
    icon: "⚽",
    color: "#4CC9F0",
    bg: "rgba(76,201,240,0.12)",
    cost: 2,
    unlockLevel: 1,
    effects: { damagePercent: 40, critEnergyRefund: 1, oncePerTurn: true },
  },
  feint: {
    id: "feint",
    name: "Feint",
    description: "Deal 40% attack damage and unbalance the enemy: your next hit deals +25% final damage. Once per turn.",
    nameFr: "Feinte",
    descFr: "Inflige 40% des dégâts d'attaque et applique Déséquilibré : ton prochain coup inflige +25% de dégâts finaux. 1 fois par tour.",
    nameKo: "페인트",
    descKo: "공격 데미지의 40%를 입히고 적의 균형을 무너뜨립니다: 다음 타격이 최종 데미지 +25%. 턴당 1회.",
    icon: "💫",
    color: "#F5B301",
    bg: "rgba(245,179,1,0.12)",
    cost: 2,
    unlockLevel: 2,
    effects: { damagePercent: 40, applyOffBalancePercent: 25, oncePerTurn: true },
  },
  reckless_strike: {
    id: "reckless_strike",
    name: "Blood Strike",
    description: "Sacrifice 10% of your max HP to deal 75% attack damage. Once per turn.",
    nameFr: "Frappe sanguinaire",
    descFr: "Perds 10% de tes PV max pour infliger 75% des dégâts d'attaque. 1 fois par tour.",
    nameKo: "피의 일격",
    descKo: "최대 체력의 10%를 희생하여 공격 데미지의 75%를 입힙니다. 턴당 1회.",
    icon: "🩸",
    color: "#FF3B47",
    bg: "rgba(255,59,71,0.12)",
    cost: 3,
    unlockLevel: 3,
    effects: { damagePercent: 75, selfDamagePercentMaxHp: 10, oncePerTurn: true },
  },
  lifesteal: {
    id: "lifesteal",
    name: "Lifesteal",
    description: "Deal 55% attack damage. On crit, heal 10% of your max HP.",
    nameFr: "Vol de vie",
    descFr: "Inflige 55% des dégâts d'attaque. Si coup critique, soigne 10% de tes PV max.",
    nameKo: "흡혈",
    descKo: "공격 데미지의 55%를 입힙니다. 치명타 시 최대 체력의 10%를 회복합니다.",
    icon: "🧛",
    color: "#2DE36B",
    bg: "rgba(45,227,107,0.12)",
    cost: 3,
    unlockLevel: 5,
    effects: { damagePercent: 55, healOnCritPercentMaxHp: 10 },
  },
  vision: {
    id: "vision",
    name: "Vision",
    description: "Deal 50% attack damage. At the end of the enemy's next turn, it takes +20% of your damage per mana it spent.",
    nameFr: "Lecture du jeu",
    descFr: "Inflige 50% des dégâts d'attaque. À la fin du prochain tour ennemi, il subit +20% de tes dégâts par mana qu'il a dépensée.",
    nameKo: "시야",
    descKo: "공격 데미지의 50%를 입힙니다. 다음 적 턴이 끝날 때, 적이 사용한 마나 1당 당신의 데미지 20%를 추가로 입힙니다.",
    icon: "👁️",
    color: "#7FA9D8",
    bg: "rgba(127,169,216,0.12)",
    cost: 3,
    unlockLevel: 7,
    effects: { damagePercent: 50, echoPerEnemyEnergyPercent: 20 },
  },
  wall: {
    id: "wall",
    name: "Wall",
    description: "Gain a 50 shield for 2 turns; +50 each time the enemy crits you.",
    nameFr: "Le Mur",
    descFr: "Gagne un bouclier de 50 pendant 2 tours ; +50 à chaque coup critique ennemi reçu.",
    nameKo: "벽",
    descKo: "2턴 동안 보호막 50을 얻습니다. 적의 치명타를 받을 때마다 +50.",
    icon: "🧱",
    color: "#4CC9F0",
    bg: "rgba(76,201,240,0.12)",
    cost: 2,
    unlockLevel: 9,
    effects: { shieldFlat: 50, shieldPerEnemyCrit: 50, shieldDurationTurns: 2 },
  },
};

/** Full catalogue ordered by unlock level (stable display order everywhere). */
export const SPELL_POOL: PveSpellId[] = (Object.keys(PVE_SPELLS) as PveSpellId[])
  .sort((a, b) => PVE_SPELLS[a].unlockLevel - PVE_SPELLS[b].unlockLevel);

/** Spells forced into every loadout (pre-selected, unremovable at spell-select). */
export const MANDATORY_SPELLS: PveSpellId[] = SPELL_POOL.filter((id) => PVE_SPELLS[id].mandatory);

/** Safe lookup — returns undefined for unknown/legacy ids. */
export function getSpell(id: string): PveSpellDef | undefined {
  return (PVE_SPELLS as Record<string, PveSpellDef>)[id];
}

/** True when the id maps to a real spell (drops legacy ids from saved loadouts). */
export function isValidSpell(id: string): id is PveSpellId {
  return getSpell(id) !== undefined;
}

/** Spells pickable at a given player level (derived — nothing stored in DB). */
export function spellsUnlockedAt(playerLevel: number): PveSpellId[] {
  return SPELL_POOL.filter((id) => PVE_SPELLS[id].unlockLevel <= playerLevel);
}

// ── Computed preview values ──────────────────────────────────────────────────
// The concrete numbers behind a spell's multipliers, from the player's LIVE
// stats/level: "80% attack damage" → 0.8 × Puissance, "level × 15 shield" →
// level × 15, HP costs/heals → % of max HP. DISPLAY ONLY — the engine
// recomputes everything at cast time (damage roll, crit, enemy defense).

export interface SpellComputedValues {
  damage?: number;          // damagePercent × Puissance (pre-roll, pre-defense — same convention as the "dmg / hit" stat hint)
  damagePerEnergy?: number; // Vision echo: extra damage per energy the ENEMY spends
  selfDamage?: number;      // HP paid on cast (Reckless Strike)
  healOnCrit?: number;      // HP restored on crit (Lifesteal)
  shield?: number;          // Wall: flat shield value
}

export function spellComputedValues(
  id: string,
  physique: number,
  maxHp: number,
  playerLevel: number
): SpellComputedValues {
  const e = getSpell(id)?.effects;
  if (!e) return {};
  const out: SpellComputedValues = {};
  if (e.damagePercent != null) out.damage = Math.round((physique * e.damagePercent) / 100);
  // Vision echo scales on a FULL base hit (100% of Puissance), not on the
  // spell's own 160% — "20% de MES dégâts" per energy the enemy spends.
  if (e.echoPerEnemyEnergyPercent != null) {
    out.damagePerEnergy = Math.round((physique * e.echoPerEnemyEnergyPercent) / 100);
  }
  if (e.selfDamagePercentMaxHp != null) out.selfDamage = Math.round((maxHp * e.selfDamagePercentMaxHp) / 100);
  if (e.healOnCritPercentMaxHp != null) out.healOnCrit = Math.round((maxHp * e.healOnCritPercentMaxHp) / 100);
  if (e.shieldFlat != null) out.shield = e.shieldFlat;
  return out;
}
